import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { ConnectionAuthKind, format } from 'shell-models-lib';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { AzureSynapseAnalyticsWorkspaceLinkedService, synapseAuthTypes } from 'app/model/linkedservice/azure-synapse-analytics.linkedservice';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { compact } from 'lodash';

const workspaceSelectionSetting: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.Synapse_Workspace_SelectionMethod,
    description: stringResources.Synapse_Workspace_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    defaultValue: '{workspaceName != null ? "manual" : "azure"}',
    isLabelVertical: true,
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'synapse-workspace-select',
                    bind: {
                        workspaceName: 'workspaceName'
                    },
                    validators: {
                        workspaceName: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable',
                    child: {
                        type: 'textbox',
                        bind: 'workspaceName',
                        label: stringResources.Synapse_Workspace_Name,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                }
            ]
        }
    ]
};

const tenantTextBoxSetting: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    }
};

const servicePrincipalIDSetting: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
};

const servicePrincipalKeySetting: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        child:
        {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SynapseWorkspace),
        stringResources.ModelName_ServicePrincipalKey)
};

export const azureSynapseAnalyticsMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(AzureSynapseAnalyticsWorkspaceLinkedService).name,
        displayName: stringResources.AzureSynapseAnalyticsWorkspace,
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureSynapseWorkspace' as ResourceKind
        },
        supportOPDG: true,
        icon: {
            className: 'svg-i-100x100-AzureSynapse'
        },
        category: ['Azure'],
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ]),
        isCompute: true,
        isFirstClass: true,
        copySinkTypes: [],
        copySourceTypes: [],
        disableQueryAPI: true,
        supportFirstClassParameterExperience: true,
        controls: [
            workspaceSelectionSetting,
            {
                type: 'select-switch',
                bind: 'authenticationType',
                label: stringResources.Authentication_Method_Label,
                options: [
                    {
                        label: stringResources.AuthenticationType_ManagedServiceIdentity,
                        value: synapseAuthTypes.MSIAuthentication
                    },
                    {
                        label: stringResources.AuthenticationType_ServicePrincipal,
                        value: synapseAuthTypes.ServicePrincipalAuthentication
                    }
                ],
                isLabelVertical: true,
                defaultValue: synapseAuthTypes.MSIAuthentication,
                cases: [
                    {
                        caseValue: synapseAuthTypes.MSIAuthentication,
                        children: [
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureDataFactory,
                                link: 'https://go.microsoft.com/fwlink/?linkid=2164350'
                            }
                        ]
                    },
                    {
                        caseValue: synapseAuthTypes.ServicePrincipalAuthentication,
                        children: [
                            tenantTextBoxSetting,
                            servicePrincipalIDSetting,
                            servicePrincipalKeySetting
                        ]
                    }
                ]
            }
        ]
    };
    return metadata;
}
